import { useState } from 'react';
import { updateServiceTypeStatusApiService } from '../api/updateServiceTypeStatusApiService';
import { SERVICE_TYPE_ERROR_MESSAGES } from '../errors';
import { SERVICE_TYPE_LABELS } from '../constants/labels';
import { ServiceTypeStatus } from '../types/serviceTypeTypes';

interface ServiceTypeStatusTarget {
  id: string;
  service_name: string;
  status: ServiceTypeStatus;
}

interface UseUpdateServiceTypeStatusOptions {
  onSuccess?: (next: { id: string; status: ServiceTypeStatus }) => void;
}

export const useUpdateServiceTypeStatus = (
  options: UseUpdateServiceTypeStatusOptions = {}
) => {
  const { onSuccess } = options;

  const [selectedServiceType, setSelectedServiceType] =
    useState<ServiceTypeStatusTarget | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleOpenDialog = (serviceType: ServiceTypeStatusTarget) => {
    setSelectedServiceType(serviceType);
    setIsDialogOpen(true);
  };

  const handleDialogClose = () => {
    if (isSubmitting) {
      return;
    }
    setIsDialogOpen(false);
    setSelectedServiceType(null);
  };

  const handleConfirm = async () => {
    if (!selectedServiceType) {
      return;
    }

    const targetStatus: ServiceTypeStatus =
      selectedServiceType.status === ServiceTypeStatus.Active
        ? ServiceTypeStatus.Inactive
        : ServiceTypeStatus.Active;
    setIsSubmitting(true);

    try {
      const result =
        await updateServiceTypeStatusApiService.updateServiceTypeStatus({
          serviceTypeId: selectedServiceType.id,
          status: targetStatus,
        });

      if (result.success) {
        const nextStatus: ServiceTypeStatus = result.status ?? targetStatus;
        setSuccessMessage(
          nextStatus === ServiceTypeStatus.Active
            ? SERVICE_TYPE_LABELS.STATUS_SUCCESS_ACTIVATED
            : SERVICE_TYPE_LABELS.STATUS_SUCCESS_DEACTIVATED
        );
        setShowSuccessSnackbar(true);
        onSuccess?.({
          id: selectedServiceType.id,
          status: nextStatus,
        });
      } else {
        setErrorMessage(
          result.error ?? SERVICE_TYPE_ERROR_MESSAGES.DEFAULT_ERROR
        );
        setShowErrorSnackbar(true);
      }
    } catch {
      setErrorMessage(SERVICE_TYPE_ERROR_MESSAGES.UNEXPECTED_ERROR);
      setShowErrorSnackbar(true);
    } finally {
      setIsSubmitting(false);
      setIsDialogOpen(false);
      setSelectedServiceType(null);
    }
  };

  return {
    selectedServiceType,
    isDialogOpen,
    isSubmitting,
    handleOpenDialog,
    handleDialogClose,
    handleConfirm,
    showSuccessSnackbar,
    setShowSuccessSnackbar,
    successMessage,
    showErrorSnackbar,
    setShowErrorSnackbar,
    errorMessage,
  };
};
